var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import getMessage from 'src/background/utils/i18n.utils';
import { joiResolver } from '@hookform/resolvers/joi';
import { KeychainKeyTypes, KeychainKeyTypesLC, } from '@interfaces/keychain.interface';
import { addToLoadingList, removeFromLoadingList, } from '@popup/multichain/actions/loading.actions';
import { setErrorMessage, setSuccessMessage, } from '@popup/multichain/actions/message.actions';
import { navigateTo, navigateToWithParams, } from '@popup/multichain/actions/navigation.actions';
import { setTitleContainerProperties } from '@popup/multichain/actions/title-container.actions';
import { fetchPhishingAccounts } from '@popup/steem/actions/phishing.actions';
import AccountUtils from '@popup/steem/utils/account.utils';
import CurrencyUtils from '@popup/steem/utils/currency.utils';
import { FavoriteUserUtils } from '@popup/steem/utils/favorite-user.utils';
import { KeysUtils } from '@popup/steem/utils/keys.utils';
import SteemUtils from '@popup/steem/utils/steem.utils';
import TokensUtils from '@popup/steem/utils/tokens.utils';
import TransferUtils from '@popup/steem/utils/transfer.utils';
import Joi from 'joi';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { BalanceSectionComponent } from 'src/common-ui/balance-section/balance-section.component';
import { OperationButtonComponent } from 'src/common-ui/button/operation-button.component';
import { FormContainer } from 'src/common-ui/form-container/form-container.component';
import { SVGIcons } from 'src/common-ui/icons.enum';
import { FormInputComponent } from 'src/common-ui/input/form-input.component';
import { InputType } from 'src/common-ui/input/input-type.enum';
import { Screen } from 'src/reference-data/screen.enum';
import { FormUtils } from 'src/utils/form.utils';
import FormatUtils from 'src/utils/format.utils';
const tokenOperationRules = FormUtils.createRules({
    receiverUsername: Joi.string().required(),
    amount: Joi.number().required().positive().max(Joi.ref('$balance')),
});
const TokensTransfer = ({ activeAccount, tokenBalance, tokenInfo, phishing, formParams, localAccounts, setErrorMessage, setSuccessMessage, navigateToWithParams, navigateTo, fetchPhishingAccounts, addToLoadingList, removeFromLoadingList, setTitleContainerProperties, }) => {
    const [balance, setBalance] = useState(formParams.balance
        ? parseFloat(formParams.balance)
        : parseFloat(tokenBalance.balance));
    const { control, handleSubmit, setValue, watch } = useForm({
        defaultValues: {
            receiverUsername: formParams.receiverUsername
                ? formParams.receiverUsername
                : '',
            amount: formParams.amount ? formParams.amount : '',
            symbol: formParams.symbol ? formParams.symbol : tokenBalance.symbol,
            memo: formParams.memo ? formParams.memo : '',
        },
        resolver: (values, context, options) => {
            const resolver = joiResolver(tokenOperationRules, { context: { balance: balance }, errors: { render: true } });
            return resolver(values, { balance: balance }, options);
        },
    });
    const [autocompleteFavoriteUsers, setAutocompleteFavoriteUsers] = useState({ categories: [] });
    useEffect(() => {
        setTitleContainerProperties({
            title: 'popup_html_transfer_tokens',
            isBackButtonEnabled: true,
        });
        fetchPhishingAccounts();
        loadAutocompleteTransferUsernames();
    }, []);
    const loadAutocompleteTransferUsernames = () => __awaiter(void 0, void 0, void 0, function* () {
        setAutocompleteFavoriteUsers(yield FavoriteUserUtils.getAutocompleteListByCategories(activeAccount.name, localAccounts));
    });
    const setAmountToMaxValue = () => {
        setValue('amount', parseFloat(balance.toString()));
    };
    const getFormParams = () => {
        return watch();
    };
    const handleClickOnSend = (form) => __awaiter(void 0, void 0, void 0, function* () {
        if (String(form.receiverUsername).trim().length === 0 ||
            form.amount.toString().trim().length === 0) {
            setErrorMessage('popup_accounts_fill');
            return;
        }
        if (parseFloat(form.amount.toString()) < 0) {
            setErrorMessage('popup_html_need_positive_amount');
            return;
        }
        if (parseFloat(form.amount.toString()) > parseFloat(balance.toString())) {
            setErrorMessage('popup_html_power_up_down_error');
            return;
        }
        if (!(yield AccountUtils.doesAccountExist(form.receiverUsername))) {
            setErrorMessage('popup_no_such_account');
            return;
        }
        const formattedAmount = `${parseFloat(form.amount.toString()).toFixed(tokenInfo.precision)} ${form.symbol}`;
        const stringifiedAmount = `${FormatUtils.formatCurrencyValue(parseFloat(form.amount.toString()), tokenInfo.precision)} ${form.symbol}`;
        let memoField = form.memo;
        if (form.memo.length) {
            if (form.memo.startsWith('#')) {
                memoField = `${form.memo} (${getMessage('popup_encrypted')})`;
                if (!activeAccount.keys.memo) {
                    setErrorMessage('popup_html_memo_key_missing');
                    return;
                }
            }
        }
        else {
            memoField = getMessage('popup_empty');
        }
        const fields = [
            { label: 'popup_html_transfer_from', value: `@${activeAccount.name}` },
            { label: 'popup_html_transfer_to', value: `@${form.receiverUsername}` },
            { label: 'popup_html_transfer_amount', value: stringifiedAmount },
            { label: 'popup_html_transfer_memo', value: memoField },
        ];
        let warningMessage = yield TransferUtils.getExchangeValidationWarning(form.receiverUsername, form.symbol, form.memo.length > 0);
        if (phishing.includes(form.receiverUsername)) {
            warningMessage = getMessage('popup_warning_phishing', [
                form.receiverUsername,
            ]);
        }
        navigateToWithParams(Screen.CONFIRMATION_PAGE, {
            method: KeychainKeyTypes.active,
            message: getMessage('popup_html_token_confirm_text'),
            fields: fields,
            warningMessage: warningMessage,
            title: 'popup_html_transfer_tokens',
            formParams: getFormParams(),
            afterConfirmAction: (options) => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    let memoParam = form.memo;
                    if (form.memo.length) {
                        if (form.memo.startsWith('#')) {
                            if (!activeAccount.keys.memo) {
                                setErrorMessage('popup_html_memo_key_missing');
                                return;
                            }
                            else {
                                memoParam = SteemUtils.encodeMemo(form.memo, activeAccount.keys.memo.toString(), yield AccountUtils.getPublicMemo(form.receiverUsername));
                            }
                        }
                    }
                    addToLoadingList('html_popup_transfer_token_operation', KeysUtils.getKeyType(activeAccount.keys.active, activeAccount.keys.activePubkey));
                    const transactionStatus = yield TokensUtils.sendToken(form.symbol, form.receiverUsername, form.amount.toString(), memoParam, activeAccount.keys.active, activeAccount.name, options);
                    if (transactionStatus.isUsingMultisig) {
                        navigateTo(Screen.HOME_PAGE, true);
                        setSuccessMessage('multisig_transaction_sent_to_signers');
                    }
                    else if (transactionStatus.broadcasted) {
                        addToLoadingList('html_popup_confirm_transaction_operation');
                        removeFromLoadingList('html_popup_transfer_token_operation');
                        if (transactionStatus.confirmed) {
                            navigateTo(Screen.HOME_PAGE, true);
                            yield FavoriteUserUtils.saveFavoriteUser(form.receiverUsername, activeAccount);
                            setSuccessMessage('popup_html_transfer_successful', [
                                `@${form.receiverUsername}`,
                                stringifiedAmount,
                            ]);
                        }
                        else {
                            setErrorMessage('popup_token_timeout');
                        }
                    }
                    else {
                        setErrorMessage('popup_html_transfer_failed');
                    }
                }
                catch (err) {
                    setErrorMessage(err.message);
                }
                finally {
                    removeFromLoadingList('html_popup_transfer_token_operation');
                    removeFromLoadingList('html_popup_confirm_transaction_operation');
                }
            }),
        });
    });
    return (React.createElement("div", { "data-testid": `${Screen.TOKENS_TRANSFER}-page`, className: "transfer-tokens-page" },
        React.createElement(BalanceSectionComponent, { value: balance, unit: watch('symbol'), label: "popup_html_balance", decimals: tokenInfo.precision }),
        React.createElement("div", { className: "caption" }, getMessage('popup_html_tokens_send_text')),
        React.createElement(FormContainer, { onSubmit: handleSubmit(handleClickOnSend) },
            React.createElement(FormInputComponent, { control: control, name: "receiverUsername", dataTestId: "input-username", type: InputType.TEXT, logo: SVGIcons.INPUT_AT, placeholder: "popup_html_username", label: "popup_html_username", autocompleteValues: autocompleteFavoriteUsers }),
            React.createElement("div", { className: "value-panel" },
                React.createElement(FormInputComponent, { classname: "currency-fake-input", dataTestId: "currency-input", control: control, name: "symbol", type: InputType.TEXT, label: "popup_html_currency", disabled: true }),
                React.createElement(FormInputComponent, { dataTestId: "amount-input", name: "amount", control: control, type: InputType.NUMBER, min: 0, placeholder: "0.000", label: "popup_html_transfer_amount", skipPlaceholderTranslation: true, rightActionClicked: setAmountToMaxValue, rightActionIcon: SVGIcons.INPUT_MAX })),
            React.createElement(FormInputComponent, { control: control, name: "memo", dataTestId: "input-memo-optional", type: InputType.TEXT, placeholder: "popup_html_memo_optional", label: "popup_html_memo_optional" }),
            React.createElement(OperationButtonComponent, { dataTestId: "button-send-tokens-transfer", requiredKey: KeychainKeyTypesLC.active, label: 'popup_html_send_transfer', onClick: handleSubmit(handleClickOnSend) }))));
};
const mapStateToProps = (state) => {
    var _a, _b, _c, _d, _e;
    return {
        activeAccount: state.steem.activeAccount,
        currencyLabels: CurrencyUtils.getCurrencyLabels((_a = state.steem.activeRpc) === null || _a === void 0 ? void 0 : _a.testnet),
        tokenBalance: (_b = state.navigation.stack[0].params) === null || _b === void 0 ? void 0 : _b.tokenBalance,
        tokenInfo: (_c = state.navigation.stack[0].params) === null || _c === void 0 ? void 0 : _c.tokenInfo,
        formParams: ((_d = state.navigation.stack[0].previousParams) === null || _d === void 0 ? void 0 : _d.formParams)
            ? (_e = state.navigation.stack[0].previousParams) === null || _e === void 0 ? void 0 : _e.formParams
            : {},
        phishing: state.steem.phishing,
        localAccounts: state.steem.accounts,
    };
};
const connector = connect(mapStateToProps, {
    setErrorMessage,
    setSuccessMessage,
    navigateToWithParams,
    navigateTo,
    fetchPhishingAccounts,
    addToLoadingList,
    removeFromLoadingList,
    setTitleContainerProperties,
});
export const TokensTransferComponent = connector(TokensTransfer);
